import React, { useState, useEffect } from 'react';
import { apiUrl } from '../config/api';
import { useNavigate } from 'react-router-dom';
import styles from './JobList.module.css';

interface JobItem {
  id: number;
  title: string;
  organization: string;
  location: string;
  type_label: string;
  close_date: string;
  department: string | null;
  formatted_salary: string;
  gender_label: string;
  no_of_jobs: number;
}

const JobList: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<JobItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    location: '',
    department: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    types: [] as string[],
    locations: [] as string[],
    departments: [] as string[]
  });

  useEffect(() => {
    fetchJobs();
  }, [filters]);

  useEffect(() => {
    // Extract unique filter options from jobs
    if (jobs.length > 0) {
      const types = [...new Set(jobs.map(job => job.type_label))];
      const locations = [...new Set(jobs.map(job => job.location))];
      const departments = [...new Set(jobs.filter(job => job.department).map(job => job.department as string))];
      
      setFilterOptions({ types, locations, departments });
    }
  }, [jobs]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.location) params.append('location', filters.location);
      if (filters.department) params.append('department', filters.department);

      const response = await fetch(apiUrl(`/api/jobs?${params.toString()}`));
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setJobs(result.data);
      } else {
        setError('Failed to fetch jobs');
      }
    } catch (err) {
      setError('Failed to load jobs');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleJobClick = (jobId: number) => {
    navigate(`/job/${jobId}`);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      location: '',
      department: ''
    });
    setSearchTerm('');
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Filter jobs based on search term
  const filteredJobs = jobs.filter(job => {
    if (!searchTerm.trim()) return true;
    
    const searchLower = searchTerm.toLowerCase().trim();
    return (
      job.title.toLowerCase().includes(searchLower) ||
      job.organization.toLowerCase().includes(searchLower) ||
      job.location.toLowerCase().includes(searchLower) ||
      (job.department && job.department.toLowerCase().includes(searchLower)) ||
      job.type_label.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <section className={styles.jobSection}>
        <div className={styles.container}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinner}></div>
            <p>Loading jobs...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={styles.jobSection}>
        <div className={styles.container}>
          <div className={styles.errorState}>
            <p className={styles.errorMessage}>⚠️ {error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.jobSection}>
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.title}>Career Opportunities</h2>
          <p className={styles.subtitle}>Join our team and make a difference</p>
        </div>

        {/* Filter Section */}
        <div className={styles.filterSection}>
          {/* Search Input */}
          <div className={styles.filterGroup}>
            <label>
              <i className="fas fa-search"></i> Search
            </label>
            <input 
              type="text"
              placeholder="Search jobs by title, organization, location..."
              value={searchTerm}
              onChange={handleSearch}
              className={styles.searchInput}
            />
          </div>

          <div className={styles.filterGroup}>
            <label>Job Type</label>
            <select 
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className={styles.filterSelect}
            >
              <option value="">All Types</option>
              {filterOptions.types.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label>Location</label>
            <select 
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              className={styles.filterSelect}
            >
              <option value="">All Locations</option>
              {filterOptions.locations.map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label>Department</label>
            <select 
              value={filters.department}
              onChange={(e) => handleFilterChange('department', e.target.value)}
              className={styles.filterSelect}
            >
              <option value="">All Departments</option>
              {filterOptions.departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <button onClick={clearFilters} className={styles.clearFiltersBtn}>
            <i className="fas fa-times"></i> Clear All
          </button>
        </div>

        {/* Results Count */}
        <div className={styles.resultsCount}>
          {filteredJobs.length} {filteredJobs.length === 1 ? 'position' : 'positions'} found
          {searchTerm && ` for "${searchTerm}"`}
          {(filters.type || filters.location || filters.department) && ' (filtered)'}
        </div>

        <div className={styles.jobList}>
          {filteredJobs.length > 0 ? (
            filteredJobs.map((job) => (
              <div 
                key={job.id} 
                className={styles.jobCard}
                onClick={() => handleJobClick(job.id)}
              >
                <div className={styles.jobHeader}>
                  <h3 className={styles.jobTitle}>{job.title}</h3>
                  <span className={`${styles.jobType} ${job.type_label === 'Full Time' ? styles.fullTime : styles.partTime}`}>
                    {job.type_label}
                  </span>
                </div>
                
                <div className={styles.jobDetails}>
                  <span className={styles.jobDetail}>
                    <i className="fas fa-building"></i>
                    {job.organization}
                  </span>
                  <span className={styles.jobDetail}>
                    <i className="fas fa-map-marker-alt"></i>
                    {job.location}
                  </span>
                  {job.department && (
                    <span className={styles.jobDetail}>
                      <i className="fas fa-tag"></i>
                      {job.department}
                    </span>
                  )}
                  <span className={styles.jobDetail}>
                    <i className="fas fa-money-bill-wave"></i>
                    {job.formatted_salary || 'Not specified'}
                  </span>
                  <span className={styles.jobDetail}>
                    <i className="fas fa-users"></i>
                    {job.gender_label}
                  </span>
                  <span className={styles.jobDetail}>
                    <i className="fas fa-user-friends"></i>
                    {job.no_of_jobs} positions
                  </span>
                  <span className={styles.jobDetail}>
                    <i className="fas fa-calendar"></i>
                    Closes: {new Date(job.close_date).toLocaleDateString()}
                  </span>
                </div>
                
                <button className={styles.viewBtn}>
                  View Details <i className="fas fa-arrow-right"></i>
                </button>
              </div>
            ))
          ) : (
            <div className={styles.emptyState}>
              <i className="fas fa-search" style={{ fontSize: '3rem', color: '#ccc', marginBottom: '15px' }}></i>
              <p>No jobs found matching your search criteria</p>
              <button onClick={clearFilters} className={styles.clearSearchBtn}>
                <i className="fas fa-undo"></i> Clear all filters
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default JobList;